/**
 * Central PIN gate for FFP ordering and WhatsApp CTAs.
 * Requires a serviceable Delhi NCR PIN (stored in localStorage) before
 * add-to-cart or WhatsApp actions proceed.
 */
import { getStoredPin } from '@theme/ffp-delivery-checker';
import { validateOrderPin } from '@theme/ffp-whatsapp-order';
import { StandardEvents } from '@shopify/events';

const GATED_SELECTOR = '[data-ffp-requires-pin]';

function isMobileViewport() {
  return window.matchMedia('(max-width: 749px)').matches;
}

/**
 * @param {HTMLElement} notice
 * @returns {string}
 */
function getBlockedNoticeText(notice) {
  const ffp = getFfpTranslations();
  const mobileBarVisible = Boolean(document.getElementById('ffp-mobile-bar')) && isMobileViewport();
  const inBuilder = Boolean(notice.closest('.ffp-builder'));

  if (mobileBarVisible && inBuilder && notice.dataset.mobileBarHint) {
    return notice.dataset.mobileBarHint;
  }

  if (isMobileViewport()) {
    return (
      notice.dataset.blockedTextShort ||
      ffp.pin_gate_blocked_short ||
      notice.dataset.blockedText ||
      ffp.pin_gate_blocked ||
      'Enter your 6-digit PIN to check delivery'
    );
  }

  return (
    notice.dataset.blockedText ||
    ffp.pin_gate_blocked ||
    'Enter your 6-digit PIN to check if we deliver to your area.'
  );
}

/**
 * @returns {Record<string, string>}
 */
function getFfpTranslations() {
  return typeof Theme !== 'undefined' && Theme.translations?.ffp ? Theme.translations.ffp : {};
}

/**
 * @param {string} [pinOverride]
 * @returns {{ allowed: boolean; pin: string | null; state: string; message: string }}
 */
export function getPinGateState(pinOverride) {
  const pin = pinOverride ?? getStoredPin() ?? '';
  const result = validateOrderPin(pin);

  return {
    allowed: result.valid,
    pin: String(pin).replace(/\D/g, '') || null,
    state: result.state,
    message: result.message,
  };
}

let pinRequiredToastTimeout;

/**
 * @param {string} message
 */
function showPinRequiredToast(message) {
  let toast = document.getElementById('ffp-pin-gate-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'ffp-pin-gate-toast';
    toast.className = 'ffp-pin-gate-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }

  toast.textContent = message;
  toast.dataset.visible = 'true';

  clearTimeout(pinRequiredToastTimeout);
  pinRequiredToastTimeout = window.setTimeout(() => {
    delete toast.dataset.visible;
  }, 5000);
}

/** Scroll to and focus the first visible PIN checker input. */
export function promptPinEntry() {
  const ffp = getFfpTranslations();
  const state = getPinGateState();
  showPinRequiredToast(ffp.pin_required_to_order || state.message);

  document.dispatchEvent(new CustomEvent('ffp:pin-required'));

  const inputs = document.querySelectorAll('[data-ffp-pin-input]');
  for (const input of inputs) {
    if (!(input instanceof HTMLElement)) continue;
    const rect = input.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;

    const container = input.closest(
      'ffp-delivery-checker, .ffp-pin-checker, .ffp-header-pin, .ffp-mobile-bar, .ffp-delivery-order-card'
    );
    container?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    input.focus();
    return;
  }
}

/**
 * @param {string} [pinOverride]
 * @returns {boolean}
 */
export function assertPinGate(pinOverride) {
  if (getPinGateState(pinOverride).allowed) return true;
  promptPinEntry();
  return false;
}

/**
 * @param {HTMLElement} el
 * @returns {HTMLElement | null}
 */
function getGateLabelEl(el) {
  return el.querySelector('[data-ffp-gate-label]');
}

/**
 * @param {HTMLElement} el
 * @param {{ allowed: boolean; state: string }} state
 */
function syncGateLabel(el, state) {
  const labelEl = getGateLabelEl(el);
  if (!labelEl) return;

  const ffp = getFfpTranslations();
  const defaultLabel = el.dataset.ffpLabelDefault || labelEl.textContent || '';
  const blockedLabel = el.dataset.ffpLabelBlocked || ffp.enter_pin_to_order || 'Enter PIN to order';
  const unavailableLabel =
    el.dataset.ffpLabelUnavailable || ffp.not_available_in_area || 'Not available in your area';

  if (state.allowed) {
    labelEl.textContent = defaultLabel;
    return;
  }

  if (state.state === 'unsupported') {
    labelEl.textContent = unavailableLabel;
    return;
  }

  labelEl.textContent = blockedLabel;
}

/**
 * @param {HTMLButtonElement} btn
 * @param {boolean} blocked
 */
function syncButtonGate(btn, blocked) {
  syncGateLabel(btn, blocked ? getPinGateState() : { allowed: true, state: 'ok' });

  if (blocked) {
    btn.setAttribute('aria-disabled', 'true');
    btn.dataset.ffpPinGated = 'true';
    btn.classList.add('ffp-pin-gated');
    return;
  }

  btn.removeAttribute('aria-disabled');
  delete btn.dataset.ffpPinGated;
  btn.classList.remove('ffp-pin-gated');
}

/**
 * @param {HTMLAnchorElement} link
 * @param {{ allowed: boolean; state: string }} state
 */
function syncLinkGate(link, state) {
  syncGateLabel(link, state);

  if (!state.allowed) {
    link.setAttribute('aria-disabled', 'true');
    link.dataset.ffpPinGated = 'true';
    link.classList.add('ffp-pin-gated');
    return;
  }

  link.removeAttribute('aria-disabled');
  delete link.dataset.ffpPinGated;
  link.classList.remove('ffp-pin-gated');
}

/**
 * @param {{ allowed: boolean; pin: string | null; state: string; message: string }} state
 */
function syncPinGateNotices(state) {
  document.querySelectorAll('[data-ffp-pin-gate-notice]').forEach((notice) => {
    if (!(notice instanceof HTMLElement)) return;

    const textEl = notice.querySelector('[data-ffp-pin-gate-notice-text]');
    const blockedText = getBlockedNoticeText(notice);
    const allowedTemplate =
      notice.dataset.allowedText ||
      getFfpTranslations().pin_gate_allowed_with_pin ||
      'Delivery to {{ pin }} confirmed · You can order now · Order at least 1 day before delivery';

    if (state.allowed && state.pin) {
      notice.hidden = false;
      notice.dataset.state = 'allowed';
      if (textEl) {
        textEl.innerHTML = allowedTemplate.includes('{{ pin }}')
          ? allowedTemplate.replace('{{ pin }}', `<strong>${state.pin}</strong>`)
          : `Delivery to <strong>${state.pin}</strong> confirmed · You can order now · Order at least 1 day before delivery`;
      }
      return;
    }

    if (state.state === 'unsupported') {
      notice.hidden = false;
      notice.dataset.state = 'blocked';
      if (textEl) textEl.textContent = state.message;
      return;
    }

    notice.hidden = false;
    notice.dataset.state = 'blocked';
    if (textEl) textEl.textContent = blockedText;
  });
}

/** Enable or disable all elements marked with data-ffp-requires-pin. */
export function syncGatedElements(pinOverride) {
  const state = getPinGateState(pinOverride);
  const blocked = !state.allowed;

  document.body.dataset.ffpPinGate = state.allowed ? 'allowed' : 'blocked';

  document.querySelectorAll(GATED_SELECTOR).forEach((el) => {
    if (el instanceof HTMLButtonElement) {
      syncButtonGate(el, blocked);
    } else if (el instanceof HTMLAnchorElement) {
      syncLinkGate(el, state);
    }
  });

  syncPinGateNotices(state);
}

function initGatedClickIntercept() {
  document.addEventListener(
    'click',
    (event) => {
      const el = event.target.closest('[data-ffp-requires-pin][data-ffp-pin-gated]');
      if (!el) return;

      event.preventDefault();
      event.stopPropagation();
      promptPinEntry();
    },
    true
  );
}

function initWaLinkIntercept() {
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest('a[href*="wa.me"]');
      if (!(link instanceof HTMLAnchorElement)) return;
      if (link.dataset.ffpWaUngated === 'true') return;
      if (link.closest('ffp-whatsapp-button, ffp-cart-whatsapp-order')) return;

      if (getPinGateState().allowed) return;

      event.preventDefault();
      event.stopPropagation();
      promptPinEntry();
    },
    true
  );
}

function init() {
  syncGatedElements();
  document.addEventListener('ffp:pin-updated', () => syncGatedElements());
  document.addEventListener('ffp:pin-validation', (event) => {
    const pin = event.detail?.pin ?? '';
    if (event.detail?.valid) {
      syncGatedElements();
      return;
    }
    if (String(pin).replace(/\D/g, '').length === 6) {
      syncGatedElements(pin);
    }
  });
  document.addEventListener(StandardEvents.productSelect, () => {
    requestAnimationFrame(() => syncGatedElements());
  });
  initGatedClickIntercept();
  initWaLinkIntercept();
}

init();
